/**
 * App-side wiring for `@heidi/coding`'s DATA seam, and nothing else.
 *
 * Packages are variant-agnostic and store-free (G1/F5): this module is where the
 * app hands coding the facts its clinical-coding transport reads but cannot
 * know — the region-resolved base URL, whether this is the desktop build, the
 * app version, the active workspace id, the tracing headers, and the shared
 * rejected-error handler tagged with the client's name — plus the app's
 * `reactQueryClient` (for module-scope invalidators). The transport itself is
 * the package's (`packages/coding/src/data/api/clinicalCodingHTTP.ts`); this
 * module supplies the ports it runs on. The scribe client family is not injected
 * here either — coding's `data/api` reads the `@heidi/api/client` registry,
 * which `scribeHTTP.ts` populates at factory time (E5). Imported at module scope
 * in `_app.tsx` right after `@/composition/hosts/workspace/workspace-data-wiring`.
 *
 * The coding settings screen's UI host used to be registered from here too, and
 * it is not any more: it lives in `src/composition/hosts/coding-settings-host.ts`
 * and is reached lazily from composition's host registry. The two configure the
 * same package from the same app, but they are opposite kinds of module — one
 * must be evaluated before the first query runs, the other must not be evaluated
 * until a screen loads — and keeping them together is what made registering the
 * host eagerly the obvious thing to do. That cost +94,291 bytes gzip on `/_app`,
 * because `_app.tsx` imports THIS module at module scope and the host names the
 * settings chrome, an app mutation delegate, the CPT disclaimer modal and the
 * package door carrying the screen.
 *
 * So the static-import list below is a bundle contract, and its suite pins it as
 * an exact allowlist: everything named here ships to every route whether or not
 * anything reaches it. Every module the ports reach was already in that list
 * transitively, through the builder this module used to import.
 */
import { isTauri } from '@/tauri/utils';
import { getClientVersion } from '@/utils/appVersion';
import { getClinicalCodingApiBaseUrl } from '@/utils/getClinicalCodingApiBaseUrl';
import { createHttpRejectedErrorHandler } from '@/utils/http/scribeHttpErrorHandler';
import { applyTracingHeaders } from '@/utils/http/tracing/tracingHeaders';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { configureCodingData } from '@heidi/coding/data';
// The door is now the only spelling: the app-path re-export this module
// deliberately did not name has been retired.
import { getWorkspaceId } from '@heidi/workspace/data';
import type { AxiosError, AxiosRequestHeaders } from 'axios';

// Built once here, not per request: the handler closes over the client name it
// logs under, and `[clinicalCodingHTTP]` is what makes a coding 401 tellable
// apart from an ml-scribe one at triage.
const handleClinicalCodingHttpRejectedError =
  createHttpRejectedErrorHandler('clinicalCodingHTTP');

configureCodingData({
  clinicalCodingTransport: {
    getBaseUrl: () => getClinicalCodingApiBaseUrl(),
    isDesktopClient: isTauri,
    getClientVersion,
    getWorkspaceId,
    // The port keeps axios's header types out of the package; the object handed
    // over is the request's own header bag, mutated in place.
    applyTracingHeaders: (headers) => applyTracingHeaders(headers as AxiosRequestHeaders),
    handleRejectedError: (error) =>
      handleClinicalCodingHttpRejectedError(error as AxiosError),
  },
  queryClient: reactQueryClient,
  // Raw value on purpose — coding's self-serve allowlist fails closed, and a
  // normalized enum (which maps unknown → dev) would open it on a misconfigured
  // prod/preview build.
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT ?? '',
});
